import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router";

import {
  SENTRY_DSN,
  SENTRY_ENVIRONMENT,
  SENTRY_RELEASE,
  SENTRY_SAMPLE_RATES,
  SENTRY_CONSOLE_LOG_LEVELS,
  getSentryTracePropagationTargets,
  isSentryEnabled,
} from "@/shared/configs/sentry.config";
import type { AxiosNoiseClassification } from "@/shared/types/sentryNoise.types";
import { scrubUrlParams, SENTRY_SCRUBBED_PARAM_PATTERNS } from "@/shared/utils/scrubUrlParams";
import { classifyAxiosNoise } from "@/shared/utils/sentryNoise";
import type {
  BrowserOptions, Breadcrumb, EventHint, ErrorEvent as SentryErrorEvent,
} from "@sentry/react";

// @sentry/react does not re-export StreamedSpanJSON; derive it from the public beforeSendSpan option.
type StreamedSpan = Parameters<NonNullable<BrowserOptions["beforeSendSpan"]>>[0];

// react-router navigation breadcrumbs carry the app URL in `to`/`from`; fetch/xhr
// breadcrumbs carry it in `url`. All three may embed filter query params.
const URL_BREADCRUMB_FIELDS = ["url", "to", "from"] as const;

const scrubBreadcrumbUrls = (breadcrumb: Breadcrumb): Breadcrumb => {
  if (!breadcrumb.data) return breadcrumb;
  for (const field of URL_BREADCRUMB_FIELDS) {
    const value = breadcrumb.data[field];
    if (typeof value === "string") {
      breadcrumb.data[field] = scrubUrlParams(value, SENTRY_SCRUBBED_PARAM_PATTERNS);
    }
  }
  return breadcrumb;
};

const applyNoiseClassification = (
  event: SentryErrorEvent,
  classification: AxiosNoiseClassification,
): SentryErrorEvent | null => {
  if (classification.kind === "drop") return null;
  if (classification.kind === "unchanged") return event;
  event.level = classification.level;
  event.fingerprint = classification.fingerprint;
  // Merge: callers already tag events (e.g. type, status) and those must survive.
  event.tags = { ...event.tags, ...classification.tags };
  return event;
};

export const sentryBeforeSend = (event: SentryErrorEvent, hint: EventHint): SentryErrorEvent | null => {
  const error = hint.originalException;
  if (error instanceof Error) {
    const isStaleChunk = error.name === "ChunkLoadError"
      || error.message.includes("dynamically imported module")
      || error.message.includes("Loading chunk")
      || error.message.includes("Loading CSS chunk")
      || error.message.includes("Unable to preload");
    if (isStaleChunk) return null;
  }

  const classifiedEvent = applyNoiseClassification(event, classifyAxiosNoise(error));
  if (!classifiedEvent) return null;

  if (classifiedEvent.request?.url) {
    classifiedEvent.request.url = scrubUrlParams(classifiedEvent.request.url, SENTRY_SCRUBBED_PARAM_PATTERNS);
  }
  classifiedEvent.breadcrumbs?.forEach(scrubBreadcrumbUrls);
  return classifiedEvent;
};

export const sentryBeforeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb =>
  scrubBreadcrumbUrls(breadcrumb);

// Streamed spans (SDK 11 default) bypass beforeSendTransaction. The SDK records the full
// fetch/XHR URL (query included) in these attributes and only sanitizes the span name; the
// segment span carries the host page URL. Cf. @sentry/core fetch.js getFetchSpanAttributes.
const SPAN_URL_ATTRIBUTE_KEYS = ["url", "http.url", "url.full"] as const;

// The SDK stores the query WITHOUT its leading "?", which scrubUrlParams needs to find it.
const SPAN_QUERY_ATTRIBUTE_KEY = "url.query";

const scrubSpanQuery = (query: string): string =>
  scrubUrlParams(`?${query}`, SENTRY_SCRUBBED_PARAM_PATTERNS).slice(1);

export const sentryBeforeSendSpan = (span: StreamedSpan): StreamedSpan => {
  for (const key of SPAN_URL_ATTRIBUTE_KEYS) {
    const value = span.attributes[key];
    if (typeof value === "string") {
      span.attributes[key] = scrubUrlParams(value, SENTRY_SCRUBBED_PARAM_PATTERNS);
    }
  }
  const query = span.attributes[SPAN_QUERY_ATTRIBUTE_KEY];
  if (typeof query === "string") {
    span.attributes[SPAN_QUERY_ATTRIBUTE_KEY] = scrubSpanQuery(query);
  }
  return span;
};

if (isSentryEnabled()) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,

    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),

      Sentry.consoleLoggingIntegration({ levels: SENTRY_CONSOLE_LOG_LEVELS }),
    ],

    tracesSampleRate: SENTRY_SAMPLE_RATES.TRACES,

    tracePropagationTargets: getSentryTracePropagationTargets(),

    replaysSessionSampleRate: SENTRY_SAMPLE_RATES.REPLAYS_SESSION,
    replaysOnErrorSampleRate: SENTRY_SAMPLE_RATES.REPLAYS_ON_ERROR,

    beforeSend: sentryBeforeSend,
    beforeSendSpan: sentryBeforeSendSpan,
    beforeBreadcrumb: sentryBeforeBreadcrumb,
  });
}

export { Sentry };

export const {
  captureException,
  captureMessage,
  captureReactException,
  setUser,
  setContext,
  setTag,
  withScope,
} = Sentry;

export const { logger } = Sentry;
